if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          dots: this.querySelector('[data-gallery-dots]'),
          zoomButton: this.querySelector('[data-gallery-zoom]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        // customização: filtrar galeria pela cor selecionada (via alt text)
        if (this.dataset.filterByColor === 'true') {
          this.initColorFilter();
        }

        // customização: autoplay de vídeo nativo ao entrar na viewport (após filtro de cor)
        if (this.dataset.videoAutoplayOnScroll === 'true') {
          this.initVideoAutoplayOnScroll();
        }

        // customização: bolinhas + lupa mobile (Bo+Tee)
        if (this.dataset.mobileDots === 'true' || this.dataset.mobileZoom === 'true') {
          this.initMobileGalleryControls();
        }

        // customização: carrossel mobile = CSS Scroll Snap nativo (Regra #10)
        this.initNativeMobileCarousel();

        // customização: toque na foto abre o zoom; arrastar continua trocando as slides
        this.initPhotoTapToZoom();

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      initMobileGalleryControls() {
        if (!this.elements.viewer) return;

        this.refreshMobileDots();
        // reconta depois do layout (vídeo/peek às vezes ainda reporta largura 0 no constructor)
        requestAnimationFrame(() => this.refreshMobileDots());
        window.addEventListener('load', () => this.refreshMobileDots(), { once: true });

        this.elements.viewer.addEventListener('slideChanged', this.onMobileSlideChanged.bind(this));

        if (this.elements.zoomButton) {
          this.elements.zoomButton.addEventListener('click', this.onMobileZoomClick.bind(this));
        }

        window.addEventListener('resize', debounce(() => this.refreshMobileDots(), 150));
      }

      getGallerySlider() {
        return this.elements.viewer?.slider || this.elements.viewer?.querySelector('[id^="Slider-"]');
      }

      /*
        Carrossel mobile: CSS Scroll Snap nativo (Regra #10 / #10i).
        GalleryViewer é <div> isolado. Sem lock manual de scroll vertical.
        JS só sincroniza dots via IntersectionObserver.
      */
      initNativeMobileCarousel() {
        this._mobileMql = window.matchMedia('(max-width: 749px)');
        this._activeSlideIndex = 0;

        const setup = () => {
          if (this._mobileMql.matches) {
            this.connectMobileSnapObserver();
          } else {
            this.disconnectMobileSnapObserver();
          }
        };

        setup();
        requestAnimationFrame(setup);
        window.addEventListener('load', setup, { once: true });
        this._mobileMql.addEventListener('change', setup);
      }

      connectMobileSnapObserver() {
        const slider = this.getGallerySlider();
        if (!slider) return;

        this.disconnectMobileSnapObserver();
        this._snapRatios = new Map();

        const visibleItems = this.getVisibleMediaItems();
        if (visibleItems.length < 2) {
          this.setActiveSlideIndex(0, visibleItems);
          return;
        }

        this._mobileSnapObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this._snapRatios.set(entry.target, entry.isIntersecting ? entry.intersectionRatio : 0);
            });

            let bestIndex = 0;
            let bestRatio = 0;
            visibleItems.forEach((item, index) => {
              const ratio = this._snapRatios.get(item) || 0;
              if (ratio > bestRatio) {
                bestRatio = ratio;
                bestIndex = index;
              }
            });

            if (bestRatio > 0) {
              this.setActiveSlideIndex(bestIndex, visibleItems);
            }
          },
          {
            root: slider,
            threshold: [0.45, 0.6, 0.75],
          }
        );

        visibleItems.forEach((item) => this._mobileSnapObserver.observe(item));
      }

      disconnectMobileSnapObserver() {
        this._mobileSnapObserver?.disconnect();
        this._mobileSnapObserver = null;
        this._snapRatios = null;
      }

      setActiveSlideIndex(index, visibleItems = this.getVisibleMediaItems()) {
        if (!visibleItems.length) return;

        const safeIndex = Math.max(0, Math.min(index, visibleItems.length - 1));
        this._activeSlideIndex = safeIndex;
        visibleItems.forEach((item, i) => item.classList.toggle('is-active', i === safeIndex));
        this.setMobileDotActive(safeIndex);
        this.elements.viewer?.classList.toggle(
          'product-gallery-viewer--last-slide',
          safeIndex >= visibleItems.length - 1
        );

        if (this.dataset.videoAutoplayOnScroll === 'true') {
          const deferred = visibleItems[safeIndex]?.querySelector('deferred-media[data-autoplay-on-scroll]');
          if (deferred) this.loadAutoplayDeferredMedia(deferred);
        }
      }

      scrollToMobileSlide(index, smooth = true) {
        const slider = this.getGallerySlider();
        const items = this.getVisibleMediaItems();
        if (!slider || !items[index]) return;

        slider.scrollTo({
          left: items[index].offsetLeft,
          behavior: smooth ? 'smooth' : 'auto',
        });
      }

      getVisibleMediaItems() {
        return Array.from(this.querySelectorAll('.product__media-list > [data-media-id]')).filter(
          (item) => !item.hasAttribute('hidden') && !item.classList.contains('hidden')
        );
      }

      refreshMobileDots() {
        if (!this.elements.dots) return;

        const visibleItems = this.getVisibleMediaItems();
        this.elements.dots.innerHTML = '';

        if (visibleItems.length <= 1) {
          this.elements.dots.hidden = true;
          this.setActiveSlideIndex(0, visibleItems);
          return;
        }

        this.elements.dots.hidden = false;
        visibleItems.forEach((item, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'product-gallery-dots__dot';
          button.dataset.index = String(index);
          button.setAttribute('aria-label', `Media ${index + 1}`);
          if (index === 0) button.setAttribute('aria-current', 'true');
          button.addEventListener('click', () => {
            this.scrollToMobileSlide(index, true);
          });
          this.elements.dots.appendChild(button);
        });

        this.setActiveSlideIndex(0, visibleItems);
        this.connectMobileSnapObserver();
      }

      setMobileDotActive(index) {
        if (!this.elements.dots) return;
        this.elements.dots.querySelectorAll('.product-gallery-dots__dot').forEach((dot, i) => {
          if (i === index) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
      }

      onMobileSlideChanged(event) {
        const current = event.detail?.currentElement;
        if (!current) return;
        const visibleItems = this.getVisibleMediaItems();
        const index = visibleItems.indexOf(current);
        if (index >= 0) this.setActiveSlideIndex(index, visibleItems);
      }

      onMobileZoomClick() {
        const active =
          this.querySelector('.product__media-list > .is-active:not([hidden])') ||
          this.getVisibleMediaItems()[0];
        this.openMediaZoom(active);
      }

      openMediaZoom(mediaItem) {
        if (!mediaItem) return;
        const toggle = mediaItem.querySelector('.product__media-toggle:not(.product__media-zoom-none)');
        toggle?.click();
      }

      /*
        Toque curto na foto abre o lightbox. Swipe horizontal/vertical não abre,
        para não brigar com o scroll-snap do carrossel nem com o scroll da página.
      */
      initPhotoTapToZoom() {
        const slider = this.getGallerySlider();
        if (!slider) return;
        if (!this.querySelector('.product__media-toggle:not(.product__media-zoom-none)')) return;

        const TAP_MOVE_PX = 12;
        const TAP_MAX_MS = 450;
        let pointerId = null;
        let startX = 0;
        let startY = 0;
        let startTime = 0;

        const isExcludedTarget = (target) =>
          Boolean(
            target.closest?.(
              '.product-gallery-mobile-controls, .slider-buttons, .thumbnail-slider, a, button, input, .deferred-media__poster-button'
            )
          );

        slider.addEventListener(
          'pointerdown',
          (event) => {
            if (event.button != null && event.button !== 0) return;
            if (isExcludedTarget(event.target)) return;
            pointerId = event.pointerId;
            startX = event.clientX;
            startY = event.clientY;
            startTime = Date.now();
          },
          { passive: true }
        );

        slider.addEventListener(
          'pointerup',
          (event) => {
            if (pointerId !== event.pointerId) return;
            pointerId = null;
            if (isExcludedTarget(event.target)) return;
            if (Date.now() - startTime > TAP_MAX_MS) return;
            if (Math.abs(event.clientX - startX) > TAP_MOVE_PX) return;
            if (Math.abs(event.clientY - startY) > TAP_MOVE_PX) return;

            const mediaItem = event.target.closest?.('.product__media-list > [data-media-id]');
            this.openMediaZoom(mediaItem);
          },
          { passive: true }
        );

        slider.addEventListener(
          'pointercancel',
          () => {
            pointerId = null;
          },
          { passive: true }
        );
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
        this.disconnectMobileSnapObserver();
        this.videoAutoplayObserver?.disconnect();
      }

      isAutoplayVideoVisible(deferredMedia) {
        if (!deferredMedia) return false;

        const slide = deferredMedia.closest('.product__media-list > [data-media-id]');
        if (slide?.hasAttribute('hidden') || slide?.classList.contains('hidden')) return false;

        const rect = deferredMedia.getBoundingClientRect();
        if (!rect.width || !rect.height) return false;

        const inViewHorizontally = rect.left < window.innerWidth && rect.right > 0;
        const inViewVertically = rect.top < window.innerHeight + 120 && rect.bottom > -120;
        return inViewHorizontally && inViewVertically;
      }

      scanVisibleAutoplayVideos() {
        if (this.dataset.videoAutoplayOnScroll !== 'true') return;

        this.querySelectorAll('deferred-media[data-autoplay-on-scroll]').forEach((deferredMedia) => {
          if (this.isAutoplayVideoVisible(deferredMedia)) {
            this.loadAutoplayDeferredMedia(deferredMedia);
          }
        });
      }

      prepareAutoplayVideo(video) {
        if (!video) return;
        video.muted = true;
        video.defaultMuted = true;
        video.autoplay = true;
        video.playsInline = true;
        video.setAttribute('autoplay', '');
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        video.loop = true;
        video.setAttribute('loop', '');
        video.controls = false;
        video.removeAttribute('controls');
        video.preload = 'auto';

        const tryPlay = () => {
          video.muted = true;
          video.play().catch(() => {});
        };

        if (video.readyState >= 2) {
          tryPlay();
        } else {
          video.addEventListener('loadeddata', tryPlay, { once: true });
          video.addEventListener('canplay', tryPlay, { once: true });
        }

        // fallback se o atributo loop falhar em alguns iOS
        video.addEventListener('ended', () => {
          video.currentTime = 0;
          tryPlay();
        });
      }

      loadAutoplayDeferredMedia(deferredMedia, force = false) {
        if (!deferredMedia) return;
        if (!force && !this.isAutoplayVideoVisible(deferredMedia)) return;
        deferredMedia.loadContent(false, false);
        const video = deferredMedia.querySelector('video');
        if (video) this.prepareAutoplayVideo(video);
      }

      initVideoAutoplayOnScroll() {
        const deferredVideos = this.querySelectorAll('deferred-media[data-autoplay-on-scroll]');
        if (!deferredVideos.length) return;

        // carrega cedo: peek lateral + margem vertical (evita demora ao deslizar)
        this.videoAutoplayObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const deferredMedia = entry.target;
              if (entry.isIntersecting) {
                this.loadAutoplayDeferredMedia(deferredMedia);
              } else {
                const video = deferredMedia.querySelector('video');
                if (video && !video.paused) video.pause();
              }
            });
          },
          { threshold: 0.01, rootMargin: '120px 40% 120px 40%' }
        );

        deferredVideos.forEach((el) => this.videoAutoplayObserver.observe(el));

        // layout ainda instável no constructor — reescaneia após paint e após load
        requestAnimationFrame(() => this.scanVisibleAutoplayVideos());
        window.addEventListener('load', () => this.scanVisibleAutoplayVideos(), { once: true });
      }

      initColorFilter() {
        const mediaItems = this.querySelectorAll('.product__media-list > [data-media-id]');
        this.hasColorTaggedMedia = Array.from(mediaItems).some((item) => item.dataset.mediaColor?.trim());

        // fallback seguro: sem alt text taggeado → não esconde nada
        if (!this.hasColorTaggedMedia) return;

        this.variantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.variantChange,
          this.onVariantChangeForColorFilter.bind(this)
        );

        this.filterMediaByColor(this.getSelectedColorValue());
      }

      onVariantChangeForColorFilter(event) {
        const sectionId = this.closest('product-info')?.dataset.section;
        if (!sectionId || event.data?.sectionId !== sectionId) return;

        // customização: updateMedia reordena o DOM mas NÃO copia o atributo hidden
        // do HTML novo — sincronizar a partir do HTML do fetch (Liquid já filtrou)
        const html = event.data?.html;
        if (html) {
          const synced = this.syncVisibilityFromHtml(html);
          if (synced) return;
        }

        if (!event.data?.variant) return;
        this.filterMediaByColor(this.getSelectedColorValue(event.data.variant));
      }

      syncVisibilityFromHtml(html) {
        const newItems = html.querySelectorAll('.product__media-list > [data-media-id]');
        if (!newItems.length) return false;

        let firstVisible = null;
        let visibleCount = 0;

        newItems.forEach((newItem) => {
          const current = this.querySelector(`.product__media-list > [data-media-id="${newItem.dataset.mediaId}"]`);
          if (!current) return;

          const shouldHide = newItem.hasAttribute('hidden');
          current.toggleAttribute('hidden', shouldHide);
          current.classList.toggle('hidden', shouldHide);

          if (!shouldHide) {
            visibleCount += 1;
            if (!firstVisible) firstVisible = current;
          } else {
            const video = current.querySelector('video');
            if (video && !video.paused) video.pause();
          }
        });

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((thumb) => {
            const mediaItem = this.querySelector(`[data-media-id="${thumb.dataset.target}"]`);
            const shouldHide = mediaItem ? mediaItem.hasAttribute('hidden') : false;
            thumb.toggleAttribute('hidden', shouldHide);
            thumb.classList.toggle('hidden', shouldHide);
          });
        }

        this.finalizeFilterState(firstVisible, visibleCount);
        return true;
      }

      getSelectedColorValue(variant) {
        const colorOptionName = (this.dataset.colorOptionName || 'Cor').trim().toLowerCase();
        let optionNames = [];

        try {
          optionNames = JSON.parse(this.dataset.productOptions || '[]');
        } catch (e) {
          optionNames = [];
        }

        const colorIndex = optionNames.findIndex((name) => String(name).trim().toLowerCase() === colorOptionName);

        if (variant && colorIndex >= 0) {
          if (Array.isArray(variant.options) && variant.options[colorIndex] != null) {
            return String(variant.options[colorIndex]);
          }
          // fallback: option1 / option2 / option3 do JSON da variante
          const keyed = variant[`option${colorIndex + 1}`];
          if (keyed != null) return String(keyed);
        }

        // fallback: ler o valor selecionado no DOM do variant picker
        const variantSelects = this.closest('product-info')?.querySelector('variant-selects');
        if (!variantSelects) return null;

        const checkedRadio = Array.from(
          variantSelects.querySelectorAll('input[type="radio"][data-option-name]')
        ).find(
          (input) =>
            input.checked && input.dataset.optionName?.trim().toLowerCase() === colorOptionName
        );
        if (checkedRadio) return checkedRadio.value;

        for (const select of variantSelects.querySelectorAll('select')) {
          const nameMatch = select.getAttribute('name')?.match(/^options\[(.+)\]$/);
          if (nameMatch && nameMatch[1].trim().toLowerCase() === colorOptionName) {
            return select.value;
          }

          const selectedOption = select.selectedOptions?.[0];
          if (selectedOption?.dataset.optionName?.trim().toLowerCase() === colorOptionName) {
            return selectedOption.value;
          }
        }

        return null;
      }

      filterMediaByColor(colorValue) {
        if (!this.hasColorTaggedMedia) return;

        const normalizedColor = colorValue?.trim().toLowerCase() || '';
        // sem cor resolvida: não desfazer o filtro (evita "mostrar tudo" por engano)
        if (!normalizedColor) return;

        const mediaItems = Array.from(this.querySelectorAll('.product__media-list > [data-media-id]'));
        let firstVisible = null;
        let visibleCount = 0;

        mediaItems.forEach((item) => {
          const mediaColor = (item.dataset.mediaColor || '').trim();
          // alt vazio = imagem comum (sempre visível)
          const shouldShow = !mediaColor || mediaColor.toLowerCase() === normalizedColor;

          item.toggleAttribute('hidden', !shouldShow);
          item.classList.toggle('hidden', !shouldShow);

          if (shouldShow) {
            visibleCount += 1;
            if (!firstVisible) firstVisible = item;
          } else {
            const video = item.querySelector('video');
            if (video && !video.paused) video.pause();
          }
        });

        // segurança: se o filtro esconderia tudo, mostra todas de novo
        if (visibleCount === 0) {
          mediaItems.forEach((item) => {
            item.removeAttribute('hidden');
            item.classList.remove('hidden');
          });
          firstVisible = mediaItems[0] || null;
          visibleCount = mediaItems.length;
        }

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((thumb) => {
            const mediaItem = this.querySelector(`[data-media-id="${thumb.dataset.target}"]`);
            const shouldHide = mediaItem ? mediaItem.hasAttribute('hidden') : false;
            thumb.toggleAttribute('hidden', shouldHide);
            thumb.classList.toggle('hidden', shouldHide);
          });
        }

        this.finalizeFilterState(firstVisible, visibleCount);
        this.refreshMobileDots();
      }

      finalizeFilterState(firstVisible, visibleCount) {
        const mediaItems = this.querySelectorAll('.product__media-list > [data-media-id]');
        mediaItems.forEach((item) => item.classList.remove('is-active'));

        if (firstVisible) {
          firstVisible.classList.add('is-active');
          firstVisible.parentElement?.scrollTo({ left: firstVisible.offsetLeft, behavior: 'auto' });
        }

        // atualiza contador mobile (1/N) só com slides visíveis
        if (this.elements.viewer?.resetPages) {
          this.elements.viewer.resetPages();
        }

        const sliderButtons = this.querySelector('.slider-buttons');
        if (sliderButtons) {
          sliderButtons.classList.toggle('small-hide', visibleCount <= 1);
        }

        requestAnimationFrame(() => this.scanVisibleAutoplayVideos());
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
              behavior: 'smooth',
            });
          }
          // customização: no mobile, window.scrollTo aqui criava “vão” branco sob o header
          if (!this.mql.matches) return;
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (!deferredMedia) return;

        if (
          deferredMedia.hasAttribute('data-autoplay-on-scroll') &&
          this.dataset.videoAutoplayOnScroll === 'true'
        ) {
          this.loadAutoplayDeferredMedia(deferredMedia, true);
          return;
        }

        deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
